import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2024 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { useEffect, useState } from "react";
import { TsDataSource } from "../services";
import { defineZone } from "../boards/utils";
import Filter from "./Filter";
import { Button, useEffectUpdate } from "..";
import { AttributeSelector, Icon } from "../general";
import { getAttributeDetail } from "../general/utils";
const PLACEHOLDER = "No filters applied, click here to add...";
const TOOLTIP_CONTENT = "A filter already exists in the filtering system. Please remove it before adding this filter.";
function RemoteFilters(props) {
    const { endpoint, baseUrl, onSave, disabledFilterValues, filterPassThrough } = props;
    const ds = new TsDataSource({ baseUrl });
    // zone component id pointer
    const filterComponentId = "remote-filters-component";
    // just keeps track of the filter ids and their order
    const [filters, setFilters] = useState(Object.keys(props.filters?.and_ || {}));
    const [disabledApplyButton, setDisabledApplyButton] = useState(true);
    const [loading, setLoading] = useState(true);
    const [entityMeta, setEntityMeta] = useState({});
    // repurposed zone so filters correctly interact with the state
    const [filterZone, setFilterZone] = useState(defineZone("dummy-object-for-remote-filters", [
        { id: filterComponentId, filter: props.filters },
    ]));
    useEffect(() => {
        ds.getEntityMeta().then((em) => {
            setEntityMeta(em);
            setLoading(false);
        });
    }, []);
    // allow to apply when changes have been made
    useEffectUpdate(() => {
        setDisabledApplyButton(false);
    }, [filterZone, filterPassThrough]);
    const removeFilter = (attribute) => {
        // update the filters that are shown
        const f = filters.filter((str) => str !== attribute);
        setFilters(f);
        // update the zone state which builds the filter ready for the api
        if (filterZone.components[filterComponentId].data.filter?.and_[attribute]) {
            const updatedComponents = { ...filterZone.components };
            delete updatedComponents[filterComponentId].data.filter?.and_[attribute];
            setFilterZone({
                ...filterZone,
                components: updatedComponents,
            });
        }
    };
    const onClean = () => {
        if (filterZone.components[filterComponentId].data.filter) {
            filterZone.components[filterComponentId].data.filter.and_ = {};
        }
        if (filterZone.components[filterComponentId].data.defaultFilter) {
            filterZone.components[filterComponentId].data.defaultFilter.and_ = {};
        }
        setFilterZone({ ...filterZone });
    };
    if (loading)
        return _jsx(_Fragment, {});
    return (_jsxs("div", { children: [_jsx(AttributeSelector, { disabledValues: disabledFilterValues, placeholder: PLACEHOLDER, attribute: filters, setAttribute: setFilters, endpoint: endpoint, baseUrl: baseUrl, populatedFieldType: "filter", numPopulatedFields: Object.keys(filterZone.components[filterComponentId].data.filter?.and_ || {}).length, tooltipContent: TOOLTIP_CONTENT, displaySource: true, recommendedFilterAvailable: true, renderSearchBySource: true, onClean: onClean }), filters.map((attribute) => {
                const attributeMeta = entityMeta?.flatAttributes?.[endpoint]?.[attribute];
                const type = attributeMeta?.cardinality < 50 &&
                    attributeMeta?.python_type === "str"
                    ? "multi"
                    : attributeMeta?.python_type;
                return (_jsxs("div", { className: "tol-filters", children: [`${getAttributeDetail(entityMeta, endpoint, attribute, 'display_name')}:`, _jsx("div", { className: "filter", children: _jsx(Filter, { attribute: attribute, rename: attributeMeta?.display_name, type: type, componentId: filterComponentId, zone: filterZone, setZone: setFilterZone, endpoint: endpoint, baseUrl: baseUrl, delay: 0 }, `filter-${attribute}`) }), _jsx("span", { className: "remove-filter-button", onClick: () => removeFilter(attribute), children: _jsx(Icon, { icon: "close", size: "lg" }) })] }, attribute));
            }), _jsx(Button, { disabled: disabledApplyButton, type: "success", onClick: () => onSave(filterZone?.components?.[filterComponentId]?.data?.filter, filterPassThrough), text: "Apply Filters", icon: "floppy-disk" })] }));
}
export default RemoteFilters;
